<template>
  <div class="plan">
    <div class="plan__title">
      <div class="plan__title-text">
        <h3 class="plan__title-name">Your Plan</h3>
        <h2 class="plan__title-content">{{ billing.planName }}</h2>
      </div>
      <div>
        <v-wait v-if="billing.allowPlanChange" for="Subscribing">
          <template v-slot:waiting>
            <span class="plan__title-btn btns btns--font-large">
              Processing...
              <button-spinner />
            </span>
          </template>
          <a
            v-if="billing.cancel_at_period_end"
            class="plan__title-btn btns btns--font-large"
            @click="reactivate"
          >
            <span> Reactivate </span>
          </a>
          <a
            v-else-if="billing.is_trial"
            class="plan__title-btn btns btns--font-large"
            @click="switchAnnualDirectly"
          >
            <span> Upgrade to Annual </span>
          </a>
          <a
            v-else
            class="plan__title-btn btns btns--font-large"
            @click="switchAnnual"
          >
            Upgrade to Annual
          </a>
        </v-wait>
        <div v-if="error !== null && error !== ''" class="subscription-error">
          {{ error }}
        </div>
        <SwitchToAnnualPopup
          ref="popupAnnual"
          :billing="billing"
          class="subscription-annual"
          @subscribed="$emit('subscribed')"
        />
      </div>
    </div>

    <div class="plan-details">
      <div class="plan-details__top">
        <div class="plan-details__top-left">
          <h3 class="plan-details-title">Details</h3>
          <p class="plan-details-name">{{ billing.plan.nickname }} Plan</p>
        </div>
        <div class="plan-details__top-middle--show-desktop">
          <a
            v-if="billing.is_trial"
            class="plan-details__switch"
            @click="switchAnnualDirectly"
            >Switch to annual and
            <span class="plan-details__save"
              >SAVE {{ billing.switchToAnnualSavings.percent }}%</span
            >
          </a>
          <a v-else class="plan-details__switch" @click="switchAnnual"
            >Switch to annual and
            <span class="plan-details__save"
              >SAVE {{ billing.switchToAnnualSavings.percent }}%</span
            >
          </a>
        </div>
        <div class="plan-details__top-right">
          <p
            v-if="billing.showPricesAfterDiscount"
            class="plan-details__top-right-text"
          >
            $12/mo, then $24/mo
          </p>
          <p v-else class="plan-details__top-right-text">
            {{ billing.plan.price.withPeriod.perMonth }}
          </p>
        </div>
      </div>
      <div
        v-if="billing.allowPlanChange"
        class="plan-details__bottom--hide-desktop"
      >
        <a
          v-if="billing.is_trial"
          class="plan-details__switch"
          @click="switchAnnualDirectly"
          >Switch to annual and
          <span class="plan-details__save"
            >SAVE {{ billing.switchToAnnualSavings.percent }}%</span
          >
        </a>
        <a v-else class="plan-details__switch" @click="switchAnnual"
          >Switch to annual and
          <span class="plan-details__save"
            >SAVE {{ billing.switchToAnnualSavings.percent }}%</span
          >
        </a>
      </div>
    </div>

    <PlanDate :billing="billing" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { BillingView } from '@/models/billing'

import SwitchToAnnualPopup from './SwitchToAnnualPopup.vue'
import PlanDate from './BillingDataPaid.PlanDate.vue'
import { PlanChangeFailedError } from '@/services/cordova.purchase.types'

const props = defineProps<{
  billing: BillingView
}>()

const wait = useWait()
const emit = defineEmits(['subscribed'])

const popupAnnual = ref<InstanceType<typeof SwitchToAnnualPopup> | null>(null)

const error = ref<string | null>(null)

function switchAnnual(): void {
  popupAnnual.value?.show()
}

async function switchAnnualDirectly(): Promise<void> {
  error.value = null
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.billing.annualPlan)
    emit('subscribed')
    wait.end('Subscribing')
  } catch (err) {
    if (err instanceof PlanChangeFailedError) {
      error.value = err.message
    }
    wait.end('Subscribing')
  }
}

async function reactivate(): Promise<void> {
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.billing.plan)
    emit('subscribed')
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
  }
}
</script>
